/**
 * UranIA PA — Dashboard widget: Azioni rapide post-login.
 *
 * Card visibile a qualsiasi utente autenticato. Mostra CTA operative
 * filtrate per ruolo (`funzionario_adg`, `beneficiario_ri`, admin):
 *
 *   - Funzionario AdG: workspace Mod5 (lista + nuovo documento BG),
 *     chat di prova al Gateway, onboarding PA (se da completare).
 *   - Beneficiario R&I: chat di prova, onboarding R&I.
 *   - Admin (platform/tenant): vedono comunque le CTA Mod5 + Chat per
 *     poter testare la piattaforma con identità privilegiata.
 *
 * Boundary:
 *   - Visibilità lato client = UX, non sicurezza. Le rotte sono
 *     comunque protette server-side (Gateway + step-up MFA per admin).
 *   - Niente Bootstrap Icons in dipendenza: icone inline SVG con
 *     ``aria-hidden`` per non sporcare lo screen reader.
 */

import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { JSX } from 'react';

import { selectPrincipal, useAuthStore } from '../../lib/auth/auth-store.ts';
import {
  principalIsAnyAdmin,
  principalIsBeneficiario,
  principalIsFunzionario,
} from '../../lib/auth/rbac.ts';

interface QuickAction {
  readonly key: string;
  readonly to: string;
  readonly testid: string;
  readonly icon: JSX.Element;
  readonly visible: boolean;
}

function IconDocument(): JSX.Element {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 0h5.5L14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2zm5 1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5H9.5A1.5 1.5 0 0 1 8 3.5V1zm1 .5V3.5a.5.5 0 0 0 .5.5h2L10 1.5z" />
    </svg>
  );
}

function IconPlus(): JSX.Element {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2z" />
    </svg>
  );
}

function IconChat(): JSX.Element {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2.678 11.894a1 1 0 0 1 .287.801 11 11 0 0 1-.398 2c1.395-.323 2.247-.697 2.634-.893a1 1 0 0 1 .71-.074A8 8 0 0 0 8 14c3.996 0 7-2.807 7-6 0-3.192-3.004-6-7-6S1 4.808 1 8c0 1.468.617 2.83 1.678 3.894M0 8c0-3.808 3.582-7 8-7s8 3.192 8 7-3.582 7-8 7a9 9 0 0 1-2.347-.306 11 11 0 0 1-3.122 1.291.7.7 0 0 1-.578-.124.73.73 0 0 1-.286-.708 9 9 0 0 0 .465-2.07C.804 11.778 0 10.02 0 8" />
    </svg>
  );
}

function IconSparkles(): JSX.Element {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M7.657 6.247c.11-.33.576-.33.686 0l.645 1.937a2.89 2.89 0 0 0 1.829 1.828l1.936.645c.33.11.33.576 0 .686l-1.937.645a2.89 2.89 0 0 0-1.828 1.829l-.645 1.936a.361.361 0 0 1-.686 0l-.645-1.937a2.89 2.89 0 0 0-1.828-1.828l-1.937-.645a.361.361 0 0 1 0-.686l1.937-.645a2.89 2.89 0 0 0 1.828-1.828l.645-1.937zM3.794 1.148a.217.217 0 0 1 .412 0l.387 1.162c.173.518.579.924 1.097 1.097l1.162.387a.217.217 0 0 1 0 .412l-1.162.387A1.734 1.734 0 0 0 4.593 5.69l-.387 1.162a.217.217 0 0 1-.412 0L3.407 5.69A1.734 1.734 0 0 0 2.31 4.593l-1.162-.387a.217.217 0 0 1 0-.412l1.162-.387A1.734 1.734 0 0 0 3.407 2.31l.387-1.162zM10.863.099a.145.145 0 0 1 .274 0l.258.774c.115.346.386.617.732.732l.774.258a.145.145 0 0 1 0 .274l-.774.258a1.156 1.156 0 0 0-.732.732l-.258.774a.145.145 0 0 1-.274 0l-.258-.774a1.156 1.156 0 0 0-.732-.732L9.1 2.137a.145.145 0 0 1 0-.274l.774-.258c.346-.115.617-.386.732-.732l.258-.774z" />
    </svg>
  );
}

function IconCompass(): JSX.Element {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16m0-1A7 7 0 1 1 8 1a7 7 0 0 1 0 14m2.288-10.082c.96-.534 2.034.54 1.5 1.5l-2.625 4.736a2 2 0 0 1-.78.78L3.648 14.56c-.96.534-2.033-.54-1.5-1.5l2.625-4.737a2 2 0 0 1 .78-.78zM8 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2" />
    </svg>
  );
}

function IconShield(): JSX.Element {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5.338 1.59a61 61 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.7 10.7 0 0 0 2.287 2.233c.346.244.652.42.893.533q.18.085.293.118a1 1 0 0 0 .101.025 1 1 0 0 0 .1-.025q.114-.034.294-.118c.24-.113.547-.29.893-.533a10.7 10.7 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.8 11.8 0 0 1-2.517 2.453 7 7 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7 7 0 0 1-1.048-.625 11.8 11.8 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 63 63 0 0 1 5.072.56z" />
      <path d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0z" />
    </svg>
  );
}

export function DashboardQuickActionsWidget(): JSX.Element | null {
  const { t } = useTranslation();
  const principal = useAuthStore(selectPrincipal);

  if (principal === null) {
    return null;
  }

  const isFunz = principalIsFunzionario(principal);
  const isRi = principalIsBeneficiario(principal);
  const isAdmin = principalIsAnyAdmin(principal);
  const isPaUser = isFunz || isAdmin;

  const actions: readonly QuickAction[] = [
    {
      key: 'open_mod5',
      to: '/workspace/mod5',
      testid: 'quick-action-open-mod5',
      icon: <IconDocument />,
      visible: isPaUser,
    },
    {
      key: 'cowork',
      to: '/cowork',
      testid: 'quick-action-cowork',
      icon: <IconSparkles />,
      visible: isPaUser,
    },
    {
      key: 'new_documento',
      to: '/workspace/mod5/new',
      testid: 'quick-action-new-documento',
      icon: <IconPlus />,
      visible: isPaUser,
    },
    {
      key: 'chat_prova',
      to: '/chat-prova',
      testid: 'quick-action-chat-prova',
      icon: <IconChat />,
      visible: isAdmin,
    },
    {
      key: 'admin_console',
      to: '/admin',
      testid: 'quick-action-admin-console',
      icon: <IconShield />,
      visible: isAdmin,
    },
    {
      key: 'onboarding_pa',
      to: '/onboarding',
      testid: 'quick-action-onboarding-pa',
      icon: <IconCompass />,
      visible: isFunz,
    },
    {
      key: 'onboarding_ri',
      to: '/onboarding',
      testid: 'quick-action-onboarding-ri',
      icon: <IconCompass />,
      visible: isRi,
    },
  ];

  const visibleActions = actions.filter((a) => a.visible);

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <article
      className="urania-dashboard__widget card h-100 shadow-sm border-0"
      data-testid="dashboard-widget-quick-actions"
      aria-labelledby="urania-dashboard-quick-actions-heading"
    >
      <div className="card-body">
        <h2 id="urania-dashboard-quick-actions-heading" className="h5 card-title mb-3">
          {t('dashboard.quick_actions.title')}
        </h2>
        <p className="text-muted small mb-3">{t('dashboard.quick_actions.subtitle')}</p>
        <ul
          className="list-unstyled row g-2 mb-0"
          aria-label={t('dashboard.quick_actions.list_aria_label')}
        >
          {visibleActions.map((action) => (
            <li key={action.key} className="col-12 col-sm-6">
              <Link
                to={action.to}
                className="btn btn-outline-primary w-100 text-start d-flex align-items-start gap-2 p-3"
                data-testid={action.testid}
              >
                <span className="flex-shrink-0">{action.icon}</span>
                <span className="d-flex flex-column">
                  <span className="fw-semibold">
                    {t(`dashboard.quick_actions.${action.key}.label`)}
                  </span>
                  <span className="small text-muted">
                    {t(`dashboard.quick_actions.${action.key}.description`)}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}
